import React from "react";
import { AlertTriangle, ExternalLink, Inbox, RefreshCw } from "lucide-react";
import { PRIORITIES } from "./constants";

/**
 * Small presentational pieces shared by the task board pages.
 */

export const formatDate = (value, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  value ? new Date(value).toLocaleDateString(undefined, opts) : "—";

export const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

const DAY = 24 * 60 * 60 * 1000;
export const daysUntil = (value) => Math.ceil((new Date(value).getTime() - Date.now()) / DAY);

/**
 * Project label: always rendered from the canonical project record (name +
 * color), looked up by id, so renames show up everywhere automatically.
 */
export const ProjectLabel = ({ project, size = "sm" }) => {
  if (!project) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 max-w-full rounded-full border font-medium ${
        size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"
      } border-line2 text-muted bg-panel2`}
      title={project.name}
    >
      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: project.color || "var(--accent)" }} />
      <span className="truncate">{project.name}</span>
    </span>
  );
};

export const PriorityBadge = ({ priority }) => {
  const p = PRIORITIES.find((x) => x.value === priority);
  if (!p) return null;
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${p.className}`}>
      {p.label}
    </span>
  );
};

export const TaskTypeBadge = ({ type }) => (
  <span
    className={`font-mono text-[10.5px] px-1.5 py-px rounded-md ${
      type === "fix"
        ? "bg-danger-soft text-danger"
        : type === "improvement"
          ? "bg-warn-soft text-warn"
          : "bg-accent-soft text-accent-ink"
    }`}
  >
    {type}
  </span>
);

const STATUS_PILL = {
  active: "pill-ok",
  planned: "pill-accent",
  completed: "bg-raise text-muted",
};

export const StatusPill = ({ status }) => (
  <span className={`pill capitalize ${STATUS_PILL[status] || STATUS_PILL.planned}`}>
    {status}
  </span>
);

export const SectionCard = ({ title, actions, children, className = "" }) => (
  <section
    className={`bg-panel backdrop-blur-xl rounded-card border border-line shadow-card p-4 sm:p-5 ${className}`}
  >
    {(title || actions) && (
      <div className="flex items-center justify-between gap-2 mb-3">
        {title && <h2 className="font-display text-[15px] font-semibold text-ink whitespace-nowrap">{title}</h2>}
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon = Inbox, title, message, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-10 px-4">
    <Icon className="w-10 h-10 text-gray-300 dark:text-dark-100 mb-3" />
    <p className="font-medium text-gray-700 dark:text-light-300">{title}</p>
    {message && <p className="text-sm text-gray-500 dark:text-light-500 mt-1 max-w-sm">{message}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 rounded-2xl border border-danger/30 bg-danger-soft text-danger">
    <AlertTriangle className="w-5 h-5 shrink-0" />
    <span className="flex-1 text-sm">{message}</span>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-xl border border-danger/40 hover:bg-danger-soft"
      >
        <RefreshCw className="w-4 h-4" /> Try again
      </button>
    )}
  </div>
);

/** Environment link with an "open in new tab" action, or an empty state. */
export const EnvironmentLink = ({ label, url }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-line">
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500 dark:text-light-500">{label}</p>
      {url ? (
        <p className="text-sm text-gray-900 dark:text-light-100 truncate" title={url}>
          {url}
        </p>
      ) : (
        <p className="text-sm italic text-gray-400 dark:text-light-500">Not configured</p>
      )}
    </div>
    {url && (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 shrink-0 text-sm font-medium px-3 py-1.5 rounded-xl text-accent-ink border border-accent-line hover:bg-accent-soft transition-colors"
      >
        Open <ExternalLink className="w-3.5 h-3.5" />
      </a>
    )}
  </div>
);

/** Client-side mirror of the backend URL rule (http/https only). */
export const isSafeUrl = (value) => {
  if (!value) return true;
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

export const SkeletonRows = ({ rows = 3 }) => (
  <div className="space-y-3 animate-pulse">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-16 rounded-xl bg-raise" />
    ))}
  </div>
);
